
window.EngoodCart = {
    timers: {},
    undoTimers: {}, // Track pending remove undo timers
    pendingKeys: new Set(), // Track items with pending updates
    syncingKeys: new Set(), // Track items actively syncing (blocked)

    async api(url, body) {
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                body: JSON.stringify(body),
            });

            return await response.json();
        } catch (e) {
            console.error('Engood Cart Error:', e);
            return null;
        }
    },

    async getCart() {
        const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        return await res.json();
    },

    // ✅ Re-render drawer from Shopify section output
    async refreshDrawer() {
        try {
            const res = await fetch(`/?sections=cart-drawer`, {
                headers: { Accept: 'application/json' },
            });
            const data = await res.json();
            if (!data || !data['cart-drawer']) return;

            // Skip refresh if there are pending updates (prevents race condition)
            if (this.pendingKeys.size > 0) {
                console.log('Skipping drawer refresh - pending updates exist');
                return;
            }

            const html = new DOMParser().parseFromString(data['cart-drawer'], 'text/html');

            // replace drawer — preserve scroll position to prevent snap-to-top on mobile
            const newDrawer = html.querySelector('#cart-drawer');
            const oldDrawer = document.querySelector('#cart-drawer');
            if (newDrawer && oldDrawer) {
                // 📌 Capture scroll position BEFORE wiping innerHTML
                const scrollBox = oldDrawer.querySelector('.custom-scrollbar');
                const savedScrollTop = scrollBox ? scrollBox.scrollTop : 0;

                // Replace inner DOM
                oldDrawer.innerHTML = newDrawer.innerHTML;

                // 📌 Restore scroll position IMMEDIATELY after replacement
                // so mobile users stay exactly where they were in the item list
                if (savedScrollTop > 0) {
                    const restoredScrollBox = oldDrawer.querySelector('.custom-scrollbar');
                    if (restoredScrollBox) restoredScrollBox.scrollTop = savedScrollTop;
                }
            }

            // replace backdrop (optional but safe)
            const newBackdrop = html.querySelector('#cart-drawer-backdrop');
            const oldBackdrop = document.querySelector('#cart-drawer-backdrop');
            if (newBackdrop && oldBackdrop) oldBackdrop.className = newBackdrop.className;

            // Restore subtotal and free-shipping opacity (dimmed during optimistic update)
            const subtotal = document.getElementById('drawer-subtotal');
            if (subtotal) subtotal.style.opacity = '1';
            const freeShipping = document.querySelector('.free-shipping-wrapper');
            if (freeShipping) freeShipping.style.opacity = '1';
        } catch (e) {
            console.error('Drawer refresh failed', e);
        }
    },

    // ✅ Update ALL product cards quantity UI and Header Icon
    syncCards(cart) {
        // Update Header Icon
        const indicator = document.getElementById('cart-indicator');
        if (indicator) {
            if (cart.item_count > 0) indicator.classList.remove('opacity-0');
            else indicator.classList.add('opacity-0');
        }

        // Update Drawer Badge (C4)
        const drawerBadge = document.getElementById('drawer-item-count');
        if (drawerBadge) {
            drawerBadge.textContent = cart.item_count;
        }

        document.querySelectorAll('.product-card-action-wrapper').forEach((wrap) => {
            const variantId = parseInt(wrap.dataset.variantId, 10);
            const item = cart.items.find((i) => i.variant_id === variantId);

            if (item) {
                // Store item.key for /cart/change.js API (it requires key, not variant_id)
                wrap.innerHTML = `
                <div class="flex items-center justify-between w-full rounded-xl overflow-hidden bg-primary/10 h-10 md:h-11 p-0.5" data-item-key="${item.key}">
                  <button type="button"
                    class="w-9 md:w-10 h-full flex items-center justify-center text-primary font-bold transition-all card-qty-btn shrink-0 hover:bg-primary/20 active:bg-primary/30"
                    data-qty="${item.quantity - 1}"
                    aria-label="Decrease quantity">
                    <span class="material-symbols-outlined text-[18px]">remove</span>
                  </button>

                  <div class="flex-1 h-full bg-white flex items-center justify-center relative px-3 min-w-0 rounded-sm">
                    <span class="font-extrabold text-text-main text-sm md:text-base font-body card-qty-display">${item.quantity}</span>
                    <span class="material-symbols-outlined text-primary text-[14px] hidden card-qty-spinner qty-spinner">sync</span>
                  </div>

                  <button type="button"
                    class="w-9 md:w-10 h-full flex items-center justify-center text-primary font-bold transition-all card-qty-btn shrink-0 hover:bg-primary/20 active:bg-primary/30"
                    data-qty="${item.quantity + 1}"
                    aria-label="Increase quantity">
                    <span class="material-symbols-outlined text-[18px]">add</span>
                  </button>
                </div>
              `;
            } else {
                wrap.innerHTML = `
                <button type="button"
                  class="w-full bg-primary hover:bg-[#1a6b1a] text-white font-bold py-2 md:py-3 px-3 md:px-6 rounded-lg md:rounded-xl transition-all duration-200 flex items-center justify-center gap-2 shadow-md h-11 md:h-12 relative"
                  data-add="1"
                  aria-label="Add to cart">
                  <span class="material-symbols-outlined text-[18px] md:text-[20px] add-btn-icon">add_shopping_cart</span>
                  <span class="text-sm md:text-base add-btn-text">Add to Cart</span>
                  <span class="material-symbols-outlined absolute text-[20px] hidden add-btn-spinner animate-spin">sync</span>
                </button>
              `;
            }
        });
    },

    // itemKey = Shopify line item key (string), quantity = new quantity
    async change(itemKey, quantity, el) {
        // If already syncing, ignore new clicks
        if (this.syncingKeys.has(itemKey)) {
            console.log('Ignoring click - item is syncing');
            return;
        }

        if (this.timers[itemKey]) clearTimeout(this.timers[itemKey]);

        // Mark this item as having pending updates
        this.pendingKeys.add(itemKey);

        // Debounced API call - block controls when timer fires
        this.timers[itemKey] = setTimeout(async () => {
            // 🔒 Mark as syncing and block controls
            this.syncingKeys.add(itemKey);
            this.blockControlsForItem(itemKey);

            await this.api('/cart/change.js', { id: itemKey, quantity });

            // Remove from pending BEFORE refreshing
            this.pendingKeys.delete(itemKey);

            // Fetch fresh cart data and drawer HTML in parallel, then update both together
            const [cart] = await Promise.all([this.getCart(), this.refreshDrawer()]);

            // Update product cards AFTER drawer is updated (so both complete together)
            this.syncCards(cart);

            // Dispatch event for other components (like wishlist) to sync
            window.dispatchEvent(new CustomEvent('cart-updated', { detail: { cart } }));

            // 🔓 Remove from syncing
            this.syncingKeys.delete(itemKey);
        }, 300);
    },

    // Block controls for a specific item (both product card and drawer row)
    blockControlsForItem(itemKey) {
        // Block product card - apply inline styles directly
        const controlsDiv = document.querySelector(`[data-item-key="${itemKey}"]`);
        if (controlsDiv) {
            controlsDiv.style.transition = 'opacity 0.3s ease';
            controlsDiv.style.opacity = '0.5';
            controlsDiv.style.pointerEvents = 'none';
            // Apply grayscale to buttons with smooth transition
            controlsDiv.querySelectorAll('button').forEach((btn) => {
                btn.style.transition = 'filter 0.3s ease, opacity 0.3s ease';
                btn.style.filter = 'grayscale(100%)';
                btn.style.opacity = '0.6';
            });
        }

        // Block drawer row - find by item key and then get parent li element
        const drawerRow = document.querySelector(`#cart-drawer li[data-item-key="${itemKey}"]`);
        if (drawerRow) {
            // Find the quantity controls container (the flex div with buttons)
            const qtyContainer = drawerRow.querySelector('.drawer-qty-btn')?.closest('.flex.items-center.justify-between');
            if (qtyContainer) {
                qtyContainer.style.transition = 'opacity 0.3s ease';
                qtyContainer.style.opacity = '0.5';
                qtyContainer.style.pointerEvents = 'none';
                qtyContainer.querySelectorAll('button').forEach((btn) => {
                    btn.style.transition = 'filter 0.3s ease, opacity 0.3s ease';
                    btn.style.filter = 'grayscale(100%)';
                    btn.style.opacity = '0.6';
                });
            }
        }
    },

    async add(variantId, quantity = 1, productTitle = '') {
        await this.api('/cart/add.js', { id: variantId, quantity });

        const cart = await this.getCart();
        this.syncCards(cart);

        await this.refreshDrawer();

        // Show mobile-friendly confirmation toast
        this.showAddToCartToast(productTitle);

        // Dispatch event for other components (like wishlist) to sync
        window.dispatchEvent(new CustomEvent('cart-updated', { detail: { cart } }));
    },

    // Instantly update card UI (optimistic update) with loading indicators
    updateCardUIInstantly(wrap, newQty) {
        // Update quantity display
        const qtyDisplay = wrap.querySelector('.card-qty-display') || wrap.querySelector('.font-bold.text-text-main');
        if (qtyDisplay) {
            qtyDisplay.textContent = newQty;
            qtyDisplay.classList.add('qty-syncing'); // Add pulse animation
        }

        // Show spinner
        const spinner = wrap.querySelector('.card-qty-spinner');
        if (spinner) spinner.classList.remove('hidden');

        // Update button data-qty values
        const btns = wrap.querySelectorAll('.card-qty-btn');
        if (btns.length >= 2) {
            btns[0].dataset.qty = Math.max(0, newQty - 1); // minus btn
            btns[1].dataset.qty = newQty + 1; // plus btn
        } else {
            // Fallback for old structure
            const minusBtn = wrap.querySelector('[data-qty]');
            const plusBtn = wrap.querySelectorAll('[data-qty]')[1];
            if (minusBtn) minusBtn.dataset.qty = Math.max(0, newQty - 1);
            if (plusBtn) plusBtn.dataset.qty = newQty + 1;
        }
    },

    // ✅ One click listener for all product cards
    bindCardEvents() {
        document.addEventListener('click', async (e) => {
            const wrap = e.target.closest('.product-card-action-wrapper');
            if (!wrap) return;

            // Stop click propagation so clicks within the action wrapper never trigger card's after:inset-0 link
            e.stopPropagation();

            const variantId = parseInt(wrap.dataset.variantId, 10);

            const addBtn = e.target.closest('[data-add]');
            if (addBtn) {
                if (addBtn.disabled) return;

                // Show loading state
                addBtn.disabled = true;
                addBtn.classList.add('opacity-80', 'cursor-not-allowed');

                const icon = addBtn.querySelector('.add-btn-icon');
                const text = addBtn.querySelector('.add-btn-text');
                const spinner = addBtn.querySelector('.add-btn-spinner');

                if (icon) icon.classList.add('invisible');
                if (text) text.classList.add('invisible');
                if (spinner) spinner.classList.remove('hidden');

                // Grab product title from the parent product-card element
                const productCard = wrap.closest('.product-card');
                const productTitle = productCard?.dataset?.title || '';

                await this.add(variantId, 1, productTitle);
                return;
            }

            const qtyBtn = e.target.closest('[data-qty]');
            if (qtyBtn) {
                const qty = parseInt(qtyBtn.dataset.qty, 10);
                const itemKeyContainer = wrap.querySelector('[data-item-key]');
                const itemKey = itemKeyContainer?.dataset?.itemKey;

                if (itemKey) {
                    // 🛡️ GUARD: If item is syncing, ignore click
                    if (this.syncingKeys.has(itemKey)) {
                        console.log('Blocked - item is syncing');
                        return;
                    }

                    // ⚡ INSTANT: Update product card UI
                    this.updateCardUIInstantly(wrap, qty);

                    // ⚡ INSTANT: Also sync drawer if open
                    const drawerRow = document.querySelector(`#cart-drawer [data-item-key="${itemKey}"]`);
                    if (drawerRow) this.updateDrawerUIInstantly(drawerRow, qty);

                    // 🕐 DELAYED: Debounced server sync
                    this.change(itemKey, qty, qtyBtn);
                }
                return;
            }
        });
    },

    // ✅ Cart drawer event handler with optimistic UI updates
    bindDrawerEvents() {
        document.addEventListener('click', async (e) => {
            const drawer = e.target.closest('#cart-drawer');
            if (!drawer) return;

            const btn = e.target.closest('[data-qty]');
            if (!btn) return;

            const row = btn.closest('[data-item-key]');
            if (!row) return;

            const itemKey = btn.dataset.itemKey || row.dataset.itemKey;
            const qty = parseInt(btn.dataset.qty, 10);

            if (itemKey) {
                // 🛡️ GUARD: If item is syncing, ignore click
                if (this.syncingKeys.has(itemKey)) {
                    console.log('Blocked - item is syncing');
                    return;
                }

                // 🛡️ GUARD: Max quantity validation (M5)
                const max = parseInt(row.dataset.max, 10) || 99;
                if (qty > max) {
                    this.showMaxLimitToast(max);
                    return;
                }

                // 🗑️ REMOVE: Intercept delete with undo toast (H2)
                if (qty === 0) {
                    const productTitle = row.querySelector('h3 a')?.textContent?.trim() || 'Item';
                    const prevQty = parseInt(row.querySelector('.drawer-qty-display')?.textContent, 10) || 1;
                    this.showUndoToast(itemKey, productTitle, prevQty, row, btn);
                    return;
                }

                // ⚡ INSTANT: Update drawer UI immediately
                this.updateDrawerUIInstantly(row, qty);

                // ⚡ INSTANT: Also sync product card if visible
                const variantId = row.dataset.variantId;
                if (variantId) {
                    const cardWrap = document.querySelector(`.product-card-action-wrapper[data-variant-id="${variantId}"]`);
                    if (cardWrap) this.updateCardUIInstantly(cardWrap, qty);
                }

                // 🕐 DELAYED: Server sync + subtotal update
                this.change(itemKey, qty, btn);
            }
        });
    },

    // ↩️ Show undo toast (H2) — delays actual remove by 5s
    showUndoToast(itemKey, productTitle, prevQty, row, btn) {
        const UNDO_DELAY = 5000;

        // Visually fade the row immediately to signal removal
        if (row) {
            row.style.transition = 'opacity 0.3s, transform 0.3s';
            row.style.opacity = '0.3';
            row.style.pointerEvents = 'none';
        }

        // Remove any existing toast for this key
        const existingToast = document.getElementById(`undo-toast-${itemKey}`);
        if (existingToast) existingToast.remove();
        if (this.undoTimers[itemKey]) clearTimeout(this.undoTimers[itemKey]);

        // Build the toast
        const toast = document.createElement('div');
        toast.id = `undo-toast-${itemKey}`;
        toast.setAttribute('role', 'alert');
        toast.className = [
            'fixed top-4 left-4 right-4 md:top-6 md:right-6 md:left-auto md:max-w-sm',
            'flex items-center gap-3',
            'bg-gray-900 text-white text-sm font-medium',
            'px-4 py-3 rounded-xl shadow-2xl z-[300]',
            'opacity-0 -translate-y-4 md:translate-y-0 md:translate-x-4 transition-all duration-300 ease-out',
            'w-auto'
        ].join(' ');

        // Progress bar countdown
        toast.innerHTML = `
            <span class="material-symbols-outlined text-red-400 shrink-0">delete</span>
            <span class="flex-1 truncate">"${productTitle}" removed</span>
            <button
                id="undo-btn-${itemKey}"
                class="shrink-0 bg-primary text-white font-bold text-xs px-3 py-1.5 rounded-lg hover:bg-[#1a6b1a] transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
                aria-label="Undo remove ${productTitle}"
            >Undo</button>
            <div class="absolute bottom-0 left-0 h-0.5 bg-primary rounded-full transition-none" id="undo-progress-${itemKey}" style="width: 100%"></div>
        `;

        document.body.appendChild(toast);

        // Animate in
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                toast.classList.remove('opacity-0', '-translate-y-4', 'md:translate-x-4');
                toast.classList.add('opacity-100', 'translate-y-0', 'translate-x-0');
                // Kick off progress bar shrink
                const progressBar = document.getElementById(`undo-progress-${itemKey}`);
                if (progressBar) {
                    progressBar.style.transition = `width ${UNDO_DELAY}ms linear`;
                    progressBar.style.width = '0%';
                }
            });
        });

        // Undo button handler
        const undoBtn = document.getElementById(`undo-btn-${itemKey}`);
        undoBtn?.addEventListener('click', () => {
            clearTimeout(this.undoTimers[itemKey]);
            delete this.undoTimers[itemKey];

            // Restore row visibility
            if (row) {
                row.style.opacity = '1';
                row.style.pointerEvents = '';
            }

            // Dismiss toast
            toast.classList.remove('opacity-100', 'translate-y-0', 'translate-x-0');
            toast.classList.add('opacity-0', '-translate-y-4', 'md:translate-x-4');
            setTimeout(() => toast.remove(), 300);
        });

        // Schedule the actual remove after delay
        this.undoTimers[itemKey] = setTimeout(() => {
            delete this.undoTimers[itemKey];

            // Dismiss toast
            toast.classList.remove('opacity-100', 'translate-y-0', 'translate-x-0');
            toast.classList.add('opacity-0', '-translate-y-4', 'md:translate-x-4');
            setTimeout(() => toast.remove(), 300);

            // Fully fade out the row, then call the server
            if (row) {
                row.style.opacity = '0';
                row.style.transform = 'translateX(-10px)';
            }

            // H4: Dispatch item-removed event to instantly update external cards (e.g. wishlist) before server response
            window.dispatchEvent(new CustomEvent('item-removed', { detail: { itemKey } }));

            this.change(itemKey, 0, btn);
        }, UNDO_DELAY);
    },

    // Instantly update drawer UI (optimistic) with loading indicators
    updateDrawerUIInstantly(row, newQty) {
        // Update quantity display with pulse animation
        const qtyDisplay = row.querySelector('.drawer-qty-display');
        if (qtyDisplay) {
            qtyDisplay.textContent = newQty;
            qtyDisplay.classList.add('qty-syncing'); // Add pulse animation
        }

        // Show spinner
        const spinner = row.querySelector('.drawer-qty-spinner');
        if (spinner) spinner.classList.remove('hidden');

        // Find buttons within the quantity controls container (parent of qtyDisplay parent)
        const qtyControlsContainer = qtyDisplay?.parentElement?.parentElement;
        if (qtyControlsContainer) {
            const qtyBtns = qtyControlsContainer.querySelectorAll('.drawer-qty-btn');
            if (qtyBtns[0]) qtyBtns[0].dataset.qty = Math.max(0, newQty - 1); // minus btn
            if (qtyBtns[1]) qtyBtns[1].dataset.qty = newQty + 1; // plus btn
        }

        // Calculate and update line price instantly
        const unitPrice = parseInt(row.dataset.unitPrice, 10) || 0;
        const linePrice = unitPrice * newQty;
        const linePriceEl = row.querySelector('.drawer-line-price');
        if (linePriceEl) {
            linePriceEl.textContent = window.formatMoney(linePrice);
        }

        // Fade out if removing
        if (newQty === 0) {
            row.style.transition = 'opacity 0.2s, transform 0.2s';
            row.style.opacity = '0';
            row.style.transform = 'translateX(-10px)';
        }

        // Calculate estimated new total across all drawer items optimistically
        let newTotal = 0;
        const allRows = document.querySelectorAll('#cart-drawer li[data-item-key]');
        allRows.forEach((r) => {
            const uPrice = parseInt(r.dataset.unitPrice, 10) || 0;
            if (r === row) {
                newTotal += uPrice * newQty;
            } else {
                const qEl = r.querySelector('.drawer-qty-display');
                const q = qEl ? parseInt(qEl.textContent, 10) || 0 : 0;
                newTotal += uPrice * q;
            }
        });

        // Optimistically update subtotal text
        const subtotal = document.getElementById('drawer-subtotal');
        if (subtotal && window.formatMoney) {
            subtotal.textContent = window.formatMoney(newTotal);
            subtotal.style.opacity = '0.7';
        }


    },

    showAddToCartToast(productTitle) {
        // Remove any existing add-to-cart toast
        const existing = document.getElementById('add-to-cart-toast');
        if (existing) existing.remove();

        const cartUrl = window.Shopify?.routes?.root ? window.Shopify.routes.root + 'cart' : '/cart';
        const displayTitle = productTitle ? `<strong class="font-bold">${productTitle}</strong> added` : 'Item added to cart';

        const toast = document.createElement('div');
        toast.id = 'add-to-cart-toast';
        // Bottom-center on mobile, top-right on desktop
        toast.className = [
            'fixed z-[300] flex items-center justify-between gap-3',
            'bottom-5 left-4 right-4 md:bottom-auto md:top-6 md:right-6 md:left-auto md:max-w-xs',
            'bg-[#1b331b] text-white px-4 py-3.5 rounded-xl shadow-2xl',
            'opacity-0 translate-y-4 md:translate-y-0 md:translate-x-4',
            'transition-all duration-300 ease-out'
        ].join(' ');

        toast.innerHTML = `
            <div class="flex items-center gap-2.5 min-w-0">
                <span class="material-symbols-outlined text-[20px] shrink-0 text-green-400">check_circle</span>
                <span class="text-sm leading-snug truncate">${displayTitle}</span>
            </div>
            <a href="${cartUrl}"
               class="shrink-0 text-xs font-bold bg-white/20 hover:bg-white/30 active:bg-white/40 px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap">
                View Cart
            </a>
        `;

        document.body.appendChild(toast);

        // Animate in
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                toast.style.opacity = '1';
                toast.style.transform = 'translate(0, 0)';
            });
        });

        // Auto-dismiss after 3.5s with fade-out
        setTimeout(() => {
            toast.style.opacity = '0';
            toast.style.transform = window.innerWidth < 768 ? 'translateY(12px)' : 'translateX(12px)';
            setTimeout(() => toast.remove(), 350);
        }, 3500);
    },

    showMaxLimitToast(max) {
        // Remove existing toast if present
        const existingToast = document.getElementById('max-limit-toast');
        if (existingToast) existingToast.remove();

        const toast = document.createElement('div');
        toast.id = 'max-limit-toast';
        toast.className = 'fixed top-4 right-4 md:top-6 md:right-6 bg-red-600 text-white px-5 py-3 rounded-lg shadow-xl z-[200] font-bold flex items-center gap-2 animate-[pulse_1s_ease-in-out_infinite]';
        toast.innerHTML = `<span class="material-symbols-outlined">warning</span> Only ${max} of this item are available.`;
        document.body.appendChild(toast);

        setTimeout(() => {
            toast.style.transition = 'opacity 0.5s ease';
            toast.style.opacity = '0';
            setTimeout(() => toast.remove(), 500);
        }, 3000);
    },

    // 💾 Persist cart note silently via AJAX (/cart/update.js)
    async saveNote(value) {
        try {
            await fetch('/cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ note: value }),
            });
        } catch (e) {
            console.error('Engood Cart: Failed to save note', e);
        }
    },

    // 💾 Bind debounced auto-save + blur save on #DrawerCartNote
    bindGiftNoteEvents() {
        // Use event delegation so it works after drawer DOM replacements
        document.addEventListener('input', (e) => {
            if (e.target.id !== 'DrawerCartNote') return;

            // Show live "Saving..." hint
            this._showNoteSavedIndicator(e.target, false);

            // Debounce 500ms — only call API after typing stops
            if (this._noteTimer) clearTimeout(this._noteTimer);
            this._noteTimer = setTimeout(() => {
                this.saveNote(e.target.value).then(() => {
                    this._showNoteSavedIndicator(e.target, true);
                });
            }, 500);
        });

        document.addEventListener('blur', (e) => {
            if (e.target.id !== 'DrawerCartNote') return;
            // On blur: cancel debounce timer and save immediately
            if (this._noteTimer) clearTimeout(this._noteTimer);
            this.saveNote(e.target.value).then(() => {
                this._showNoteSavedIndicator(e.target, true);
            });
        }, true); // capture: true so blur fires on this non-bubbling event
    },

    // Show a subtle "Saving..." / "Saved ✓" indicator near the textarea
    _showNoteSavedIndicator(textarea, isSaved) {
        let indicator = document.getElementById('note-save-indicator');
        if (!indicator) {
            indicator = document.createElement('span');
            indicator.id = 'note-save-indicator';
            indicator.className = 'absolute right-2 bottom-2 text-[10px] font-semibold transition-opacity duration-300 pointer-events-none';
            // Make textarea wrapper relative if not already
            const wrapper = textarea.parentElement;
            if (wrapper && getComputedStyle(wrapper).position === 'static') {
                wrapper.style.position = 'relative';
            }
            textarea.parentElement?.appendChild(indicator);
        }

        if (isSaved) {
            indicator.textContent = 'Saved ✓';
            indicator.style.color = 'var(--color-primary, #228B22)';
            indicator.style.opacity = '1';
            // Auto-fade after 2s
            clearTimeout(this._noteIndicatorTimer);
            this._noteIndicatorTimer = setTimeout(() => {
                indicator.style.opacity = '0';
            }, 2000);
        } else {
            clearTimeout(this._noteIndicatorTimer);
            indicator.textContent = 'Saving...';
            indicator.style.color = 'var(--color-text-subtle, #8a8a7a)';
            indicator.style.opacity = '1';
        }
    },

    async init() {
        this.bindCardEvents();
        this.bindDrawerEvents();
        this.bindGiftNoteEvents();
        const cart = await this.getCart();
        this.syncCards(cart);
    },
};

document.addEventListener('DOMContentLoaded', () => {
    window.EngoodCart.init();
});